/**
 * Logger utility for the HappyTech frontend application
 *
 * Provides logging for missing translations and other i18n-related events.
 * This supports FR-016: System MUST log missing translation keys with timestamp.
 */

export type LogLevel = 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

/**
 * Logger class for consistent logging across the application
 */
class Logger {
  private prefix = '[HappyTech]';

  /**
   * Log an informational message
   */
  info(message: string, context?: Record<string, unknown>): void {
    this.log('info', message, context);
  }

  /**
   * Log a warning message
   */
  warn(message: string, context?: Record<string, unknown>): void {
    this.log('warn', message, context);
  }

  /**
   * Log an error message
   */
  error(message: string, context?: Record<string, unknown>): void {
    this.log('error', message, context);
  }

  /**
   * Log a missing translation key (FR-016)
   */
  logMissingTranslation(key: string, lang: string): void {
    this.warn(`Missing translation: ${key} for language: ${lang}`, {
      key,
      lang,
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });
  }

  /**
   * Internal log method
   */
  private log(level: LogLevel, message: string, context?: Record<string, unknown>): void {
    const entry: LogEntry = {
      level,
      message: `${this.prefix} ${message}`,
      timestamp: new Date().toISOString(),
      context,
    };

    const logMethod = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;

    if (context) {
      logMethod(entry.message, context);
    } else {
      logMethod(entry.message);
    }

    // In development, you might want to send this to a monitoring service
    if (import.meta.env.DEV && typeof window !== 'undefined' && (window as any).analytics) {
      (window as any).analytics.track('log_entry', entry);
    }
  }
}

// Export singleton instance
export const logger = new Logger();
